/*
 *  (c) 2015 - 2024 ENISCO by FORCAM GmbH
 */

import React, {useEffect, useState} from 'react'
import {ListItemIcon, ListItemText, MenuItem} from '@mui/material'
import {AddToQueue, RemoveFromQueue} from '@mui/icons-material'
import {FormattedMessage} from 'react-intl'
import useCurrentApp from '../../../../hooks/useCurrentApp'
import useProfile from '../../../../hooks/useProfile'
import {AppId} from '../../../../model/apps'
import {Profile} from '../../../../model/profiles'

interface Props {
  onClose: () => void
  open: boolean
}

export const QuickLaunchMenu: React.FC<Props> = ({onClose, open}) => {
  const [currentApp] = useCurrentApp()
  const [profile, setProfile] = useProfile()

  const calculateAlreadyAdded = (p?: Profile, a?: AppId) => {
    return (
      (p?.quickLaunchApps &&
        p?.quickLaunchApps.some(
          (app) =>
            app.app === a?.app &&
            app.module === a.module &&
            app.favoriteId === a.favoriteId
        )) ||
      false
    )
  }

  const [alreadyAdded, setAlreadyAdded] = useState(
    calculateAlreadyAdded(profile, currentApp)
  )

  useEffect(() => {
    if (currentApp) setAlreadyAdded(calculateAlreadyAdded(profile, currentApp))
  }, [currentApp, open, profile])

  const closeMenuAndAdd = () => {
    if (currentApp) {
      const currentQuickLaunchApps = Object.assign([], profile?.quickLaunchApps)

      currentQuickLaunchApps.push(currentApp)

      if (profile?.quickLaunchType === undefined || profile?.quickLaunchType === 'none') {
        setProfile({
          quickLaunchApps: currentQuickLaunchApps,
          quickLaunchType: 'left',
          quickLaunchCustomized: true
        })
      } else {
        setProfile({quickLaunchApps: currentQuickLaunchApps, quickLaunchCustomized: true})
      }
    }
    onClose()
  }

  const closeMenuAndRemove = () => {
    if (currentApp) {
      const currentQuickLaunchApps = Object.assign(
        [],
        profile?.quickLaunchApps
      ).filter((app: AppId) => {
        return !(
          app.app === currentApp.app &&
          app.module === currentApp.module &&
          app.favoriteId === currentApp.favoriteId
        )
      })

      setProfile({quickLaunchApps: currentQuickLaunchApps, quickLaunchCustomized: true})
    }
    onClose()
  }

  const close = () => {
    if (alreadyAdded) closeMenuAndRemove()
    else closeMenuAndAdd()
  }

  return (
    <MenuItem data-testid='QuickLaunchMenu:item' onClick={close}>
      <ListItemIcon>
        {alreadyAdded ? (
          <RemoveFromQueue color={'primary'} />
        ) : (
          <AddToQueue color={'primary'} />
        )}
      </ListItemIcon>
      <ListItemText>
        {alreadyAdded ? (
          <FormattedMessage
            id='ProfileOptions.RemoveFromQuicklaunchBar'
            defaultMessage='Remove from quicklaunch bar'
          />
        ) : (
          <FormattedMessage
            id='ProfileOptions.QuicklaunchBar'
            defaultMessage='Add to quicklaunch bar'
          />
        )}
      </ListItemText>
    </MenuItem>
  )
}
